// The account flow's ENTRY PARAMETERS, and the one place they are parsed.
//
// The flow used to read `window.location.search` from seven places inside
// itself. That is what made it a page rather than a component: it could only
// run somewhere that carried its arguments in the address bar, and a screen
// wanting to host it had no way to supply them.
//
// Now a host parses once and passes the result in. `/authorize` parses the
// URL; a dialog on another screen supplies its own values and never touches
// the address bar. The flow itself reads neither.
//
// Dependency-free on purpose, like accountFlowRouting: a host must be able to
// build these without React, and a test must be able to import them without
// dragging the page's graph.

export interface AccountFlowParams {
  /** Pre-fills the entry field. Inert on its own. */
  email?: string
  /** The sending screen already collected the email and acted on it, so the
   *  entry step is skipped and the lookup runs immediately. */
  skipEmailEntry?: boolean
  /** A paper key handed in directly — the QR / deep-link restore path. */
  paperKey?: string
  /** The paper key came from a kit printed with the pre-2026-09-21 QR (the
   *  query form, with the user id and email beside the words). Such a kit
   *  deserves an updated one after the restore. Set by the page host from
   *  paperKeyHandoff; never parsed from a query string here. */
  paperKeyFromPrintedKit?: boolean
  /** 'migration' forces the legacy paper-key migration on entry. */
  flow?: string
  /** Copy shown above the email field, replacing the default prompt. */
  loginPrompt?: string
  /** Where the host should return the user once the flow completes. The FLOW
   *  never navigates to this — it is carried so a host can. */
  fromUri?: string
}

/** Reads one value, tolerating the array form query parsers can produce. */
function one(v: string | string[] | null | undefined): string | undefined {
  if (Array.isArray(v)) return v.length > 0 ? v[0] : undefined
  return v === null || v === undefined ? undefined : v
}

// A flag is on when present and not explicitly negated. Query strings are
// written by hand and by other pages, so `?flag`, `=1` and `=true` must all
// mean the same thing; only `false` and `0` turn it off.
function flag(v: string | undefined): boolean {
  if (v === undefined) return false
  const s = String(v).toLowerCase()
  return s !== 'false' && s !== '0'
}

/**
 * Parse the account flow's parameters out of a query string.
 *
 * Accepts the string with or without its leading `?`. Unknown parameters are
 * ignored — the OAuth set in particular is the page host's business and never
 * reaches the flow.
 *
 * `email` is trimmed and lowercased here, the same normalisation the entry
 * field applies to what a person types, so a pass-through and a keystroke
 * produce the identical lookup.
 */
export function paramsFromSearch(search: string | null | undefined): AccountFlowParams {
  const q = new URLSearchParams((search ?? '').replace(/^\?/, ''))
  const email = one(q.get('email'))
  const params: AccountFlowParams = {}
  if (email !== undefined && email.trim() !== '') params.email = email.trim().toLowerCase()
  if (flag(one(q.get('skipEmailEntry')))) params.skipEmailEntry = true
  const paperKey = one(q.get('paper_key'))
  if (paperKey) params.paperKey = paperKey
  const flow = one(q.get('flow'))
  if (flow) params.flow = flow
  const loginPrompt = one(q.get('login_prompt'))
  if (loginPrompt) params.loginPrompt = loginPrompt
  const fromUri = one(q.get('fromUri'))
  if (fromUri) params.fromUri = fromUri
  return params
}

/**
 * Where a host should send the user when the flow finishes.
 *
 * Same origin only. The return address arrives in the sign-in page's query,
 * so anyone can write one: a link to the genuine `/authorize/?fromUri=…`
 * that, after a real sign-in on the real site, lands the person on a page
 * of the link-writer's choosing (an open redirect, found in the 2026-10-15
 * review). The target is parsed the way the browser will parse it on
 * navigation and followed only when it resolves to this site's own origin
 * over the same scheme. Anything else, and anything that will not parse,
 * goes home.
 *
 * `origin` is the site's own, `window.location.origin` unless given. A
 * caller with no window (a server render, a node test) passes it; with
 * neither, only a relative target can be known to stay on the site, and
 * only one is accepted.
 *
 * Every reader of a return address comes through here: the page host's
 * signed-in and contact-info hops and its "Continue" button
 * (`authorize.tsx`), the contact-info page's Save and the email
 * confirmation's redirect (`update_contact_info.tsx`, `confirm_email.tsx`),
 * and the legacy email login's callback (`LoginCallback.tsx`).
 *
 * Defaults to '/' exactly as the three navigation sites did before they were
 * consolidated — `params.get('fromUri') || '/'`.
 */
export function returnTarget(params: AccountFlowParams, origin?: string): string {
  const target = params.fromUri
  if (typeof target !== 'string' || target === '') return '/'
  return isSameOrigin(target, origin) ? target : '/'
}

/**
 * Whether `target`, parsed as the browser will parse it on navigation,
 * resolves to the site's own origin over the same scheme. The check behind
 * returnTarget, on its own for the one redirect target that is not a return
 * address: the legacy email login's `redirect_uri`, which the emailed link
 * carries back exactly as the sign-in page's query had it and whose callback
 * follows it with the session tokens in tow (useAuth.tsx).
 *
 * `origin` is the site's own, `window.location.origin` unless given. With
 * neither, only a relative target can be known to stay on the site. A
 * relative target IS same-origin here; a caller that needs an absolute URL
 * checks that itself.
 */
export function isSameOrigin(target: string, origin?: string): boolean {
  const here = origin ?? (typeof window !== 'undefined' ? window.location.origin : undefined)
  // With no origin to compare against, the placeholder base makes a relative
  // target resolve onto it and an absolute one resolve elsewhere.
  const base = here ?? 'https://origin.invalid'
  try {
    const self = new URL(base)
    const url = new URL(target, base)
    // The protocol check is for blob: URLs, whose origin reports the one
    // they were minted on.
    return url.origin === self.origin && url.protocol === self.protocol
  } catch {
    return false
  }
}

/**
 * The page host's target for the contact-info step, carrying the return
 * address in the query.
 *
 * The SLASH form, deliberately. This hop is a full page load, and the
 * deployed site's origin is an S3 website endpoint: a slash-less page URL
 * gets S3's own 302 to the slash form, which drops the query string — and
 * with it the return address (practicemd-aws src/cloudfront.tf, header;
 * practicemd-api docs/Analytics-and-Observability.md, "Link forms"). The dev
 * server keeps the query on its redirect, so only the slash form behaves the
 * same in both. 2026-10-15: a new user who followed a payment-form link
 * finished sign-up on the home page because this was `?fromUri=` after a
 * slash-less path. Every full page load the site performs must be written
 * this way, as useAuth writes `/authorize/` and `/callback/`.
 *
 * The return address it carries has been through `returnTarget`, so an
 * off-site one is already home by the time the contact-info page reads it.
 */
export function contactInfoHref(params: AccountFlowParams, origin?: string): string {
  return `/account/update_contact_info/?fromUri=${encodeURIComponent(returnTarget(params, origin))}`
}
